import Hls from 'hls.js';
import {
  BUFFER_RECOVERY_THRESHOLD,
  LATENCY_CATCH_UP_THRESHOLD,
  LATENCY_SLOW_DOWN_THRESHOLD,
} from './constants';

export const LATENCY_TARGET_SECONDS = 2.7;

export type CorrectionState = 'none' | 'catchUp' | 'slowDown';

const CATCH_UP_RATE = 1.08;
const SLOW_DOWN_RATE = 0.92;
const TARGET_LATENCY = LATENCY_TARGET_SECONDS;
const EPSILON = 0.03;
/**
 * Buffer kept ahead of the startup seek position. Seeking closer than this to
 * the buffered edge empties the buffer and stalls playback right after the
 * spinner is dropped, which drives latency back up.
 */
const STARTUP_SEEK_MARGIN_SECONDS = 0.6;
/**
 * Latency this close to the target needs no startup seek — the playback-rate
 * controller absorbs the remainder without a visible jump. Waiting for an exact
 * match here is what previously kept the startup spinner up until the timeout.
 */
const STARTUP_ALIGNED_TOLERANCE_SECONDS = 0.5;

export type InitialLatencyAlignmentResult = 'waiting' | 'corrected' | 'aligned';

/**
 * Align the first playable LL-HLS frame using the measured live edge.
 * hls.js uses liveSyncDuration as a loading hint, but the first selected part
 * can begin earlier. Seek once to the measured target, but only while the
 * target position is buffered with room to spare — the first latency estimate
 * of a session can be stale, and seeking onto the buffered edge because of it
 * stalls playback instead of stabilising it.
 *
 * 'aligned'   — already within tolerance, nothing to do.
 * 'corrected' — the seek landed on the target; startup is done.
 * 'waiting'   — no usable estimate, or the target is not safely buffered yet.
 */
export function alignInitialLiveLatency(
  hls: Hls,
  video: HTMLVideoElement,
  logger: { debug: (...args: unknown[]) => void },
): InitialLatencyAlignmentResult {
  if (hls.lowLatencyMode !== true || video.readyState < 1) return 'waiting';

  const latency = hls.latency;
  if (!Number.isFinite(latency) || latency <= 0) return 'waiting';

  const correction = latency - LATENCY_TARGET_SECONDS;
  if (Math.abs(correction) <= STARTUP_ALIGNED_TOLERANCE_SECONDS) return 'aligned';

  const currentTime = video.currentTime;
  const targetTime = currentTime + correction;
  for (let i = 0; i < video.buffered.length; i++) {
    const start = video.buffered.start(i);
    const safeEnd = video.buffered.end(i) - STARTUP_SEEK_MARGIN_SECONDS;
    if (targetTime < start || targetTime > safeEnd) continue;

    video.currentTime = targetTime;
    const direction = correction > 0 ? 'forward' : 'backward';
    logger.debug(
      `[latency:start] ${latency.toFixed(2)}s → seeking ${Math.abs(correction).toFixed(2)}s ${direction}` +
        ` to ${LATENCY_TARGET_SECONDS.toFixed(2)}s target`,
    );
    return 'corrected';
  }

  return 'waiting';
}

export function applyLatencyControl(
  hls: Hls,
  video: HTMLVideoElement,
  logger: { debug: (...args: unknown[]) => void },
  normalRate: number = 1,
  correctionState: CorrectionState = 'none',
  targetLatency: number = TARGET_LATENCY,
): CorrectionState {
  if (hls.lowLatencyMode !== true) return 'none';

  const latency = hls.latency;
  if (latency == null) return 'none';

  if (latency > jumpThreshold(targetLatency)) {
    const correction = latency - targetLatency;
    const seekTarget = video.currentTime + correction;
    video.currentTime = seekTarget;
    if (Math.abs(normalRate - video.playbackRate) > EPSILON) {
      video.playbackRate = normalRate;
    }
    logger.debug(`[latency:jump] ${latency.toFixed(2)}s → seeking ${correction.toFixed(2)}s forward to land ${targetLatency.toFixed(2)} s before live edge`);
    return 'none';
  }

  let targetRate: number;
  let newState: CorrectionState;

  if (correctionState === 'catchUp' && latency > targetLatency) {
    targetRate = CATCH_UP_RATE * normalRate;
    newState = 'catchUp';
  } else if (correctionState === 'slowDown' && latency < targetLatency) {
    targetRate = SLOW_DOWN_RATE * normalRate;
    newState = 'slowDown';
  } else if (latency >= catchUpThreshold(targetLatency)) {
    if (correctionState !== 'catchUp') {
      logger.debug(`[latency:catchUp] ${latency.toFixed(2)}s → playbackRate ${CATCH_UP_RATE * normalRate}`);
    }
    targetRate = CATCH_UP_RATE * normalRate;
    newState = 'catchUp';
  } else if (latency <= slowDownThreshold(targetLatency)) {
    if (correctionState !== 'slowDown') {
      logger.debug(`[latency:slowDown] ${latency.toFixed(2)}s → playbackRate ${SLOW_DOWN_RATE * normalRate}`);
    }
    targetRate = SLOW_DOWN_RATE * normalRate;
    newState = 'slowDown';
  } else {
    targetRate = normalRate;
    newState = 'none';
  }

  if (Math.abs(targetRate - video.playbackRate) > EPSILON) {
    video.playbackRate = targetRate;
  }

  return newState;
}

// ─── Lag instability detector ─────────────────────────────────────────────────
// Watches for sustained instability while latency stays above `threshold`.
// It triggers fallback when either:
// 1) no meaningful (percent-based) improvement is observed for NO_PROGRESS_TIMEOUT_MS,
// 2) latency jitter remains high for JITTER_TIMEOUT_MS.

/** How long (ms) the stream must stay above threshold with no improvement before triggering. */
const NO_PROGRESS_TIMEOUT_MS = 60_000;

/** Minimum percent improvement (vs episode baseline) required to reset the no-progress window. */
const MIN_PROGRESS_RATIO = 0.08;

/** Require at least this much elapsed time before evaluating meaningful progress. */
const MIN_PROGRESS_EVAL_MS = 5_000;

/** Window and threshold for jitter-based instability detection. */
const JITTER_WINDOW_MS = 15_000;
const JITTER_MIN_SAMPLES = 8;
const JITTER_STDDEV_THRESHOLD = 0.25;
const JITTER_TIMEOUT_MS = 60_000;
const EPISODE_ROLLOVER_MS = 10 * 60_000;

type LatencySample = { at: number; value: number };

export interface LagInstabilityDetector {
  /** Called on each LEVEL_UPDATED event with the current hls.latency. */
  check(latency: number, isPaused: boolean): void;
  /** Reset all state — call on engine restart / MANIFEST_PARSED. */
  reset(): void;
}

/*
This detector starts an instability episode only when latency rises above the configured threshold value.
During an episode it triggers fallback if there is no meaningful progress for 60,000 ms, or if high jitter persists for 60,000 ms.
Meaningful progress requires at least an 8% latency reduction from the episode baseline, evaluated only after 5,000 ms to ignore short-term noise.
Jitter is measured from samples in the last 15,000 ms, requiring at least 8 samples and a standard deviation of at least 0.25 seconds before the jitter timer runs.
State is cleared on recovery/pause/reset/trigger, and a periodic rollover every 600,000 ms refreshes baselines and sample history to avoid stale memory buildup.
*/

export function createLagInstabilityDetector(options: {
  /** Static threshold, or a getter when it follows the adaptive latency target. */
  threshold: number | (() => number);
  onTriggered: () => void;
}): LagInstabilityDetector {
  const getThreshold = (): number =>
    typeof options.threshold === 'function' ? options.threshold() : options.threshold;
  let episodeStartTime: number | null = null;
  let episodeStartLatency: number | null = null;
  let bestLatencyInEpisode: number | null = null;
  let jitterStartTime: number | null = null;
  let samples: LatencySample[] = [];

  const clearState = (): void => {
    episodeStartTime = null;
    episodeStartLatency = null;
    bestLatencyInEpisode = null;
    jitterStartTime = null;
    samples = [];
  };

  const getStdDev = (values: number[]): number => {
    if (values.length === 0) return 0;
    const mean = values.reduce((acc, v) => acc + v, 0) / values.length;
    const variance = values.reduce((acc, v) => {
      const d = v - mean;
      return acc + d * d;
    }, 0) / values.length;
    return Math.sqrt(variance);
  };

  return {
    check(latency: number, isPaused: boolean): void {
      if (latency <= 0 || isPaused) {
        clearState();
        return;
      }

      const now = Date.now();

      if (latency <= getThreshold()) {
        // Latency recovered below threshold — close current instability episode.
        clearState();
        return;
      }

      if (episodeStartTime === null) {
        episodeStartTime = now;
        episodeStartLatency = latency;
        bestLatencyInEpisode = latency;
        samples = [{ at: now, value: latency }];
        return;
      }

      // Periodically roll over the tracking episode so long-running sessions
      // do not keep stale baseline state indefinitely.
      if (now - episodeStartTime >= EPISODE_ROLLOVER_MS) {
        episodeStartTime = now;
        episodeStartLatency = latency;
        bestLatencyInEpisode = latency;
        jitterStartTime = null;
        samples = [{ at: now, value: latency }];
        return;
      }

      samples.push({ at: now, value: latency });
      samples = samples.filter(sample => now - sample.at <= JITTER_WINDOW_MS);

      if (bestLatencyInEpisode == null || latency < bestLatencyInEpisode) {
        bestLatencyInEpisode = latency;
      }

      const baselineLatency = episodeStartLatency ?? latency;
      const elapsedSinceEpisodeStart = now - episodeStartTime;
      const progressRatio = baselineLatency > 0
        ? (baselineLatency - (bestLatencyInEpisode ?? baselineLatency)) / baselineLatency
        : 0;

      // Reset no-progress timer only for meaningful progress, not micro-jitter.
      if (
        elapsedSinceEpisodeStart >= MIN_PROGRESS_EVAL_MS &&
        progressRatio >= MIN_PROGRESS_RATIO
      ) {
        episodeStartTime = now;
        episodeStartLatency = bestLatencyInEpisode ?? latency;
        bestLatencyInEpisode = bestLatencyInEpisode ?? latency;
      } else if (elapsedSinceEpisodeStart >= NO_PROGRESS_TIMEOUT_MS) {
        options.onTriggered();
        clearState();
        return;
      }

      if (samples.length >= JITTER_MIN_SAMPLES) {
        const stdDev = getStdDev(samples.map(sample => sample.value));
        if (stdDev >= JITTER_STDDEV_THRESHOLD) {
          jitterStartTime ??= now;
          if (now - jitterStartTime >= JITTER_TIMEOUT_MS) {
            options.onTriggered();
            clearState();
            return;
          }
        } else {
          jitterStartTime = null;
        }
      }
    },
    reset(): void {
      clearState();
    },
  };
}

// ─── Adaptive latency target ──────────────────────────────────────────────────
// A fixed 2.7s target makes recovery from a stall self-defeating: the buffer is
// rebuilt by the stall itself, then catch-up burns it straight back down to the
// target, so the next hiccup — a late part, a long GOP on a stream with an
// unstable keyframe interval — stalls again with no headroom left.
//
// The target therefore carries a small elevation above the base: each stall
// episode adds a step, and clean playback bleeds it away continuously. A one-off
// hiccup is forgotten within about a minute of healthy playback; only stalls
// that keep coming back faster than the elevation decays hold the target up, and
// even then it never exceeds LATENCY_TARGET_MAX_SECONDS. Everything derived from
// the target (catch-up / slow-down thresholds, the buffer level that resumes
// latency control, the instability threshold) moves with it, so an elevated
// target buys real buffer instead of just relabelling the same playhead position.

/** Upper bound for the adaptive target — beyond this LL-HLS is not worth holding. */
export const LATENCY_TARGET_MAX_SECONDS = 6;
/** Added to the target on each stall episode. */
const TARGET_STALL_STEP_SECONDS = 0.6;
/** Elevation bled off per second of stall-free playback — one step per ~60s. */
const TARGET_DECAY_PER_SECOND = 0.01;
/** The margin a stall just bought is held this long before it starts decaying. */
const TARGET_DECAY_GRACE_MS = 15_000;
/**
 * Floor for a stall penalty even after recovery is reported, so a stream that
 * flaps in and out of recovery cannot ratchet the target up in a few seconds.
 */
const STALL_PENALTY_COOLDOWN_MS = 8_000;
/**
 * Longest gap between healthy updates that still counts as playback time. A
 * larger gap means the tab was hidden or playback was paused — time that must
 * not be credited as proof the stream is stable.
 */
const MAX_HEALTHY_GAP_MS = 5_000;

/** Start catching up this far above the current target. */
const CATCH_UP_MARGIN_SECONDS = LATENCY_CATCH_UP_THRESHOLD - LATENCY_TARGET_SECONDS;
/** Start slowing down this far below the current target. */
const SLOW_DOWN_MARGIN_SECONDS = LATENCY_TARGET_SECONDS - LATENCY_SLOW_DOWN_THRESHOLD;
/** Above target + this margin the drift is seeked away instead of rate-corrected. */
const JUMP_MARGIN_SECONDS = 10 - LATENCY_TARGET_SECONDS;
/** Buffer required below the target before latency control resumes after a stall. */
const RECOVERY_MARGIN_SECONDS = LATENCY_TARGET_SECONDS - BUFFER_RECOVERY_THRESHOLD;
/** Hard ceiling for that requirement, whatever the target. */
const RECOVERY_CAP_SECONDS = 4;

export const catchUpThreshold = (target: number): number => target + CATCH_UP_MARGIN_SECONDS;
export const slowDownThreshold = (target: number): number => target - SLOW_DOWN_MARGIN_SECONDS;
export const jumpThreshold = (target: number): number => target + JUMP_MARGIN_SECONDS;
/**
 * Buffer required to resume latency control after a stall. It follows the
 * target but is capped: an LL-HLS buffer ends close to the live edge, so a
 * requirement of "almost the whole target" could never be met and would leave
 * latency control suspended for good.
 */
export const bufferRecoveryThreshold = (target: number): number =>
  Math.min(target - RECOVERY_MARGIN_SECONDS, RECOVERY_CAP_SECONDS);

/** Hysteresis between the suspend and resume conditions below. */
const SUSPEND_HYSTERESIS_SECONDS = 0.3;

/**
 * Whether a thin buffer is a reason to suspend latency control.
 *
 * Suspension exists to stop catch-up from eating a buffer that is already thin.
 * Below the target there is no catch-up to suppress — only the slow-down branch,
 * which is exactly what rebuilds the buffer — so suspending there would strand
 * the player at rate 1.0 with a buffer that can never reach the resume level.
 * The margin keeps this from flipping against the resume condition every frag.
 */
export const shouldSuspendForBuffer = (
  latency: number | null | undefined,
  target: number,
): boolean => latency != null && latency > target + SUSPEND_HYSTERESIS_SECONDS;

export interface AdaptiveLatencyTarget {
  /** The latency the controller currently steers towards, in seconds. */
  get(): number;
  /**
   * Called while a stall / buffer-underrun episode is reported. Only the first
   * report of an episode raises the target: an episode that takes a while to
   * clear must not be charged once per report.
   */
  noteStall(): void;
  /** Called when the buffer recovered and latency control resumed. */
  noteRecovered(): void;
  /**
   * Called on every playlist update while the picture is actually moving.
   * Bleeds the stall elevation away in proportion to that playback time.
   */
  noteHealthy(): void;
  /** Back to the base target — call on engine restart. */
  reset(): void;
}

export function createAdaptiveLatencyTarget(
  logger?: { debug: (...args: unknown[]) => void },
  now: () => number = Date.now,
): AdaptiveLatencyTarget {
  /** Seconds held above the base target because of recent stalls. */
  let elevation = 0;
  let lastPenaltyAt = -Infinity;
  let lastStallAt = -Infinity;
  let lastHealthyAt: number | null = null;
  /** True between the first stall report and the recovery that clears it. */
  let inStallEpisode = false;

  const maxElevation = LATENCY_TARGET_MAX_SECONDS - LATENCY_TARGET_SECONDS;

  return {
    get: () => LATENCY_TARGET_SECONDS + elevation,

    noteStall(): void {
      const at = now();
      lastStallAt = at;
      // One episode, one step. Without this a stall that takes half a minute to
      // clear would be charged once per report and drive the target to its cap.
      if (inStallEpisode) return;
      inStallEpisode = true;
      if (at - lastPenaltyAt < STALL_PENALTY_COOLDOWN_MS) return;
      lastPenaltyAt = at;
      const next = Math.min(maxElevation, elevation + TARGET_STALL_STEP_SECONDS);
      if (next === elevation) return;
      elevation = next;
      logger?.debug(
        `[latency:target] stall — raising target to ${(LATENCY_TARGET_SECONDS + elevation).toFixed(2)}s` +
          ' to keep buffer headroom',
      );
    },

    noteRecovered(): void {
      inStallEpisode = false;
    },

    noteHealthy(): void {
      const at = now();
      const previous = lastHealthyAt;
      lastHealthyAt = at;
      if (elevation <= 0) return;
      // Let the margin a stall just bought survive the moments right after it.
      if (at - lastStallAt < TARGET_DECAY_GRACE_MS) return;
      if (previous === null) return;
      const elapsedMs = at - previous;
      if (elapsedMs <= 0 || elapsedMs > MAX_HEALTHY_GAP_MS) return;

      elevation = Math.max(0, elevation - (elapsedMs / 1000) * TARGET_DECAY_PER_SECOND);
      if (elevation === 0) {
        logger?.debug(
          `[latency:target] stable — back to the ${LATENCY_TARGET_SECONDS.toFixed(2)}s base target`,
        );
      }
    },

    reset(): void {
      elevation = 0;
      lastPenaltyAt = -Infinity;
      lastStallAt = -Infinity;
      lastHealthyAt = null;
      inStallEpisode = false;
    },
  };
}
